import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, Grid, Divider, Paper, 
  Chip, CircularProgress, useTheme, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, alpha, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  TextField, Alert, Select, MenuItem, InputLabel,
  ToggleButton, ToggleButtonGroup, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import MemoryIcon from '@mui/icons-material/Memory';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { fetchRequest as downloadTraining } from '../../redux/actions/trainings/download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from '../../utils/datetime';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import {
  fetchRequest as trainingDetailRequest,
  clearState as trainingDetailClear,
} from '../../redux/actions/trainings/detail';
import { promoteRequest, clearState } from '../../redux/actions/promotion/promote';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';

// TabPanel component for the tabs
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`training-detail-tabpanel-${index}`}
      aria-labelledby={`training-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Comparison view (forecast vs actual, residuals, per-window stats)
// ---------------------------------------------------------------------
// Self-contained component for the held-out validation pass produced
// by the trainer. Two modes:
//
//   * "aggregate" (default): one stat strip across ALL held-out
//     points + an overlay chart of every window + a horizon-decay
//     chart that shows mean |residual| per forecast step.
//   * "per-window": paginated single-window view with its own line +
//     residuals + per-window stats (kept for drill-down debugging).
//
// Backend writes ``individual_comparisons`` in the segment's ORIGINAL
// units (post inverse-RevIN), so all charts read in the same units
// the user sees in the segment preview.
const ComparisonView = ({ report, theme }) => {
  const allWindows = (report && report.individual_comparisons) || [];
  const windowCount = allWindows.length;
  // ALL hooks must be declared up-front, before any early return,
  // so React sees the same hook call order on every render.
  // ``bandMode`` defaults are tuned later in this function but the
  // initial value can be a constant ('minmax') - the user can flip
  // to P25-P75 via the toggle.
  const [viewMode, setViewMode] = useState('aggregate');
  const [windowIdx, setWindowIdx] = useState(0);
  const [bandMode, setBandMode] = useState('minmax');

  // Number formatter that keeps small/large values readable.
  const fmt = (v, digits = 2) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    const abs = Math.abs(v);
    if (abs >= 1e6) return `${(v / 1e6).toFixed(digits)}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(digits)}k`;
    if (abs >= 1) return v.toFixed(digits);
    return v.toFixed(Math.max(digits, 4));
  };
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);
  const mean = (arr) => (arr.length ? sum(arr) / arr.length : 0);

  // Normalise every window to length-H pairs of (predicted, actual).
  // Inconsistent lengths are clamped to the shorter side.
  const cleanedWindows = allWindows
    .map((w) => {
      const gt = (w.ground_truth || []).slice(0, 365);
      const pr = (w.predicted || []).slice(0, gt.length);
      const len = Math.min(gt.length, pr.length);
      return { gt: gt.slice(0, len), pr: pr.slice(0, len) };
    })
    .filter((w) => w.gt.length > 0);

  if (cleanedWindows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No comparison data with usable shape.
      </Typography>
    );
  }
  const horizonLen = Math.max(...cleanedWindows.map((w) => w.gt.length));

  // Stat card used both modes.
  const StatCard = ({ label, value, hint, valueColor }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        bgcolor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: valueColor || theme.palette.text.primary,
          lineHeight: 1.2,
          mt: 0.25,
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );

  // Common Nivo theme so every chart matches the rest of the modal.
  const nivoTheme = {
    background: 'transparent',
    text: { fill: theme.palette.text.primary, fontSize: 11 },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.6),
        strokeDasharray: '2 4',
      },
    },
    crosshair: {
      line: {
        stroke: theme.palette.primary.main,
        strokeWidth: 1,
        strokeOpacity: 0.4,
      },
    },
    axis: {
      domain: { line: { stroke: alpha(theme.palette.text.secondary, 0.5) } },
      ticks: {
        line: { stroke: alpha(theme.palette.text.secondary, 0.5) },
        text: { fill: theme.palette.text.secondary, fontSize: 11 },
      },
      legend: {
        text: { fill: theme.palette.text.primary, fontSize: 12, fontWeight: 600 },
      },
    },
    legends: { text: { fill: theme.palette.text.primary, fontSize: 12 } },
    tooltip: {
      container: {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.palette.boxShadow,
        borderRadius: 4,
        padding: '8px 10px',
        border: `1px solid ${theme.palette.divider}`,
      },
    },
  };

  const StatStripGrid = ({ stats }) => (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="MAE" value={fmt(stats.mae)} hint="mean absolute error" />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="RMSE" value={fmt(stats.rmse)} hint="root mean squared error" />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard
          label="MAPE"
          value={stats.mape === null ? '—' : `${stats.mape.toFixed(1)}%`}
          hint="mean absolute % error"
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard
          label="Bias"
          value={`${stats.meanBias >= 0 ? '+' : ''}${fmt(stats.meanBias)}`}
          hint={stats.meanBias >= 0 ? 'systematic over-forecast' : 'systematic under-forecast'}
          valueColor={
            Math.abs(stats.meanBias) < stats.mae * 0.2
              ? theme.palette.success.main
              : stats.meanBias >= 0
              ? theme.palette.warning.main
              : theme.palette.error.main
          }
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard
          label="Max over"
          value={`+${fmt(stats.maxOver >= 0 ? stats.maxOver : 0)}`}
          hint="largest over-prediction"
          valueColor={theme.palette.warning.main}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard
          label="Max under"
          value={fmt(stats.maxUnder <= 0 ? stats.maxUnder : 0)}
          hint="largest under-prediction"
          valueColor={theme.palette.error.main}
        />
      </Grid>
    </Grid>
  );

  const computeStats = (residuals, truths) => {
    if (residuals.length === 0) {
      return { mae: 0, rmse: 0, mape: null, meanBias: 0, maxOver: 0, maxUnder: 0 };
    }
    const absRes = residuals.map(Math.abs);
    const mae = mean(absRes);
    const mse = mean(absRes.map((x) => x * x));
    const rmse = Math.sqrt(mse);
    const meanBias = mean(residuals);
    const maxOver = Math.max(...residuals);
    const maxUnder = Math.min(...residuals);
    const mapeArr = absRes
      .map((e, i) => (truths[i] !== 0 ? e / Math.abs(truths[i]) : null))
      .filter((v) => v !== null);
    const mape = mapeArr.length ? mean(mapeArr) * 100 : null;
    return { mae, rmse, mape, meanBias, maxOver, maxUnder };
  };

  // -----------------------------------------------------------------
  // Aggregate-mode data: flatten every (truth, predicted) pair from
  // every window. This is what the user actually compares across runs.
  // -----------------------------------------------------------------
  const allTruth = cleanedWindows.flatMap((w) => w.gt);
  const allPred = cleanedWindows.flatMap((w) => w.pr);
  const allRes = allPred.map((p, i) => p - allTruth[i]);
  const aggStats = computeStats(allRes, allTruth);

  // Horizon decay: average |residual| AT each forecast step across
  // all windows. Reveals how error grows with forecast distance.
  const horizonDecay = Array.from({ length: horizonLen }, (_, step) => {
    const values = cleanedWindows
      .filter((w) => step < w.gt.length)
      .map((w) => Math.abs(w.pr[step] - w.gt[step]));
    return {
      x: String(step),
      step: step + 1,
      mae: values.length ? mean(values) : 0,
      n: values.length,
    };
  });

  // -----------------------------------------------------------------
  // Confidence-band data for the aggregate "All Windows" view.
  // -----------------------------------------------------------------
  // We replace the per-window spaghetti (one line per window) with
  // TWO consensus lines (mean Actual, mean Predicted) plus TWO
  // shaded bands showing how much each varies across windows. This
  // is the textbook forecasting visualisation: same information
  // density as the spaghetti, but the brain only has to track 2
  // lines + 2 ranges instead of 2N lines.
  //
  // ``bandMode`` is declared at the top of the component (with the
  // other hooks) so React's hook-order rule isn't broken by the
  // early ``return`` above for empty windows. The user can flip
  // between min/max (full range) and P25-P75 (interquartile,
  // ignores outliers when N is large) via the toggle in the chart
  // header.

  // Percentile of a numeric array. Linear interpolation.
  const percentile = (arr, p) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sorted[lo];
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  };

  // Per-step aggregates: mean (consensus line) + low/high (band).
  // Computed for both actual and predicted at every forecast step.
  const stepAggregates = Array.from({ length: horizonLen }, (_, step) => {
    const aVals = cleanedWindows.filter((w) => step < w.gt.length).map((w) => w.gt[step]);
    const pVals = cleanedWindows.filter((w) => step < w.pr.length).map((w) => w.pr[step]);
    let aLow, aHigh, pLow, pHigh;
    if (bandMode === 'p25-p75') {
      aLow = percentile(aVals, 0.25);
      aHigh = percentile(aVals, 0.75);
      pLow = percentile(pVals, 0.25);
      pHigh = percentile(pVals, 0.75);
    } else {
      aLow = aVals.length ? Math.min(...aVals) : 0;
      aHigh = aVals.length ? Math.max(...aVals) : 0;
      pLow = pVals.length ? Math.min(...pVals) : 0;
      pHigh = pVals.length ? Math.max(...pVals) : 0;
    }
    return {
      step,
      aMean: mean(aVals),
      pMean: mean(pVals),
      aLow, aHigh, pLow, pHigh,
      n: aVals.length,
    };
  });
  const meanActual = stepAggregates.map((s) => s.aMean);
  const meanPred = stepAggregates.map((s) => s.pMean);

  // Two consensus lines only - no spaghetti. The bands are drawn
  // by a custom Nivo layer under the lines.
  const overlaySeries = [
    {
      id: 'Mean Actual',
      _kind: 'mean-actual',
      data: meanActual.map((v, idx) => ({ x: idx, y: v })),
    },
    {
      id: 'Mean Predicted',
      _kind: 'mean-predicted',
      data: meanPred.map((v, idx) => ({ x: idx, y: v })),
    },
  ];

  // -----------------------------------------------------------------
  // Per-window mode data (existing single-window view).
  // -----------------------------------------------------------------
  const safeIdx = Math.min(Math.max(windowIdx, 0), Math.max(0, windowCount - 1));
  const win = cleanedWindows[safeIdx] || cleanedWindows[0];
  const groundTruth = win.gt;
  const predicted = win.pr;
  const winResiduals = predicted.map((p, i) => p - groundTruth[i]);
  const winStats = computeStats(winResiduals, groundTruth);

  // Per-window line series.
  const lineData = [
    {
      id: 'Actual',
      data: groundTruth.map((v, i) => ({ x: i, y: v })),
    },
    {
      id: 'Predicted',
      data: predicted.map((v, i) => ({ x: i, y: v })),
    },
  ];

  // Per-window residual bars - colour-coded by sign.
  const residualData = winResiduals.map((r, i) => ({
    x: String(i),
    residual: r,
    color: r >= 0 ? theme.palette.warning.main : theme.palette.error.main,
  }));

  // Tooltip for the per-window line chart - shows actual / predicted
  // / error for the hovered timestep all in one row, with sign-aware
  // colour. (``nivoTheme`` and ``StatCard`` are defined once near the
  // top of this component and shared between both view modes.)
  const linePointTooltip = ({ point }) => {
    const i = point.data.x;
    const a = groundTruth[i];
    const p = predicted[i];
    const e = p - a;
    const pctErr = a !== 0 ? ((e / Math.abs(a)) * 100) : null;
    return (
      <Box sx={{
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        px: 1.5, py: 1,
        boxShadow: theme.palette.boxShadow,
        minWidth: 180,
      }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Step {i + 1} of {groundTruth.length}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
          <Typography variant="body2">Actual</Typography>
          <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
            {fmt(a)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
          <Typography variant="body2">Predicted</Typography>
          <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
            {fmt(p)}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Error</Typography>
          <Typography
            variant="body2"
            sx={{
              ml: 'auto',
              fontWeight: 600,
              color: e >= 0 ? theme.palette.warning.main : theme.palette.error.main,
            }}
          >
            {e >= 0 ? '+' : ''}{fmt(e)}
            {pctErr !== null ? ` (${(pctErr >= 0 ? '+' : '')}${pctErr.toFixed(1)}%)` : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  // ---- Theme-resilient line colours -------------------------------
  // Why this matters: many MUI themes (including this one) have
  // ``primary.main`` and ``success.main`` in the same hue family,
  // which makes "Actual" and "Predicted" lines indistinguishable
  // when both use those palette slots. Anchor Actual to the neutral
  // text colour - it's always high-contrast against the chart bg in
  // any theme. Predicted keeps the brand primary so the chart still
  // feels themed. The DualLineLayer below additionally dashes
  // Predicted so the two are distinguishable even where they
  // overlap.
  const ACTUAL_COLOR = alpha(theme.palette.text.primary, 0.85);
  const PRED_COLOR = theme.palette.primary.main;

  const overlayColorFor = (series) => {
    switch (series._kind || series.data?.[0]?._kind) {
      case 'mean-actual':
        return ACTUAL_COLOR;
      case 'mean-predicted':
        return PRED_COLOR;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Custom Nivo layer that draws the two main lines ourselves so we
  // can give Predicted a dashed stroke (nivo's built-in stroke
  // pattern only fills areas, not strokes). Used by both the
  // confidence-band aggregate chart and the per-window line chart.
  // ``actualKind`` / ``predKind`` lets the caller specify which
  // ``_kind`` value identifies each series (the per-window chart
  // uses 'actual'/'predicted' from default ids; the aggregate chart
  // uses 'mean-actual'/'mean-predicted').
  const makeDualLineLayer = ({ actualSeriesId, predSeriesId }) =>
    ({ series, lineGenerator, xScale, yScale }) => {
      if (!series || series.length < 2) return null;
      const findSeries = (id) =>
        series.find((s) => s.id === id) || null;
      const actualSeries = findSeries(actualSeriesId);
      const predSeries = findSeries(predSeriesId);
      const buildPath = (s) =>
        s
          ? lineGenerator(
              s.data.map((p) => ({ x: xScale(p.data.x), y: yScale(p.data.y) })),
            )
          : null;
      return (
        <g>
          {actualSeries && (
            <path
              d={buildPath(actualSeries)}
              fill="none"
              stroke={ACTUAL_COLOR}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          {predSeries && (
            <path
              d={buildPath(predSeries)}
              fill="none"
              stroke={PRED_COLOR}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
        </g>
      );
    };

  // Layer instances bound to the right series ids for each mode.
  const AggregateLineLayer = makeDualLineLayer({
    actualSeriesId: 'Mean Actual',
    predSeriesId: 'Mean Predicted',
  });
  const PerWindowLineLayer = makeDualLineLayer({
    actualSeriesId: 'Actual',
    predSeriesId: 'Predicted',
  });

  // Custom Nivo layer: shaded confidence bands drawn UNDER the
  // consensus lines. One polygon per series (actual + predicted)
  // bounded by per-step low/high. Translucent fill in the matching
  // hue so the band is "the same series, fanned out".
  const ConfidenceBandLayer = ({ xScale, yScale }) => {
    if (!stepAggregates.length) return null;
    const buildBand = (loKey, hiKey) => {
      const top = stepAggregates.map((s) =>
        `${xScale(s.step)},${yScale(s[hiKey])}`
      );
      const bottom = stepAggregates
        .map((s) => `${xScale(s.step)},${yScale(s[loKey])}`)
        .reverse();
      return `M ${top.join(' L ')} L ${bottom.join(' L ')} Z`;
    };
    return (
      <g>
        <path
          d={buildBand('aLow', 'aHigh')}
          fill={alpha(theme.palette.success.main, 0.16)}
          stroke="none"
        />
        <path
          d={buildBand('pLow', 'pHigh')}
          fill={alpha(theme.palette.primary.main, 0.16)}
          stroke="none"
        />
      </g>
    );
  };

  // Tooltip for the overlay chart: shows the consensus reading at
  // the hovered step PLUS the band range so the user can see
  // "where in the spread the model landed".
  const overlayTooltip = ({ point }) => {
    const i = point.data.x;
    const agg = stepAggregates[i];
    if (!agg) return null;
    const e = agg.pMean - agg.aMean;
    const pctErr = agg.aMean !== 0 ? ((e / Math.abs(agg.aMean)) * 100) : null;
    const bandLabel = bandMode === 'p25-p75' ? 'P25–P75' : 'min–max';
    return (
      <Box sx={{
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        px: 1.5, py: 1,
        boxShadow: theme.palette.boxShadow,
        minWidth: 240,
      }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Step {i + 1} · {agg.n} window{agg.n === 1 ? '' : 's'} · band: {bandLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.success.dark || theme.palette.success.main }} />
          <Typography variant="body2">Mean Actual</Typography>
          <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>{fmt(agg.aMean)}</Typography>
        </Box>
        <Typography variant="caption" sx={{ ml: 2.25, color: theme.palette.text.secondary, display: 'block' }}>
          range: {fmt(agg.aLow)} – {fmt(agg.aHigh)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.primary.dark || theme.palette.primary.main }} />
          <Typography variant="body2">Mean Predicted</Typography>
          <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>{fmt(agg.pMean)}</Typography>
        </Box>
        <Typography variant="caption" sx={{ ml: 2.25, color: theme.palette.text.secondary, display: 'block' }}>
          range: {fmt(agg.pLow)} – {fmt(agg.pHigh)}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Mean Error</Typography>
          <Typography
            variant="body2"
            sx={{
              ml: 'auto',
              fontWeight: 600,
              color: e >= 0 ? theme.palette.warning.main : theme.palette.error.main,
            }}
          >
            {e >= 0 ? '+' : ''}{fmt(e)}
            {pctErr !== null ? ` (${(pctErr >= 0 ? '+' : '')}${pctErr.toFixed(1)}%)` : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Renders the aggregate view (default).
  const renderAggregate = () => (
    <>
      <StatStripGrid stats={aggStats} />

      {/* ---- Confidence-band chart ---- */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
          mb: 1.5,
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Forecast vs Actual · Consensus + spread across {cleanedWindows.length} windows
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Solid lines are the per-step mean. Shaded bands show how
              much each varies across windows ({bandMode === 'p25-p75' ? 'inter-quartile range' : 'min – max'}).
              Tight bands = consistent; wide bands = variable.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ToggleButtonGroup
              value={bandMode}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setBandMode(v); }}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 1, py: 0.25,
                  fontSize: '0.7rem',
                  borderColor: theme.palette.divider,
                },
                '& .Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              }}
            >
              <ToggleButton value="minmax">Min–Max</ToggleButton>
              <ToggleButton value="p25-p75">P25–P75</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 14, height: 2.5, bgcolor: ACTUAL_COLOR }} />
                <Typography variant="caption">Actual</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{
                  width: 14, height: 2.5,
                  background: `repeating-linear-gradient(90deg, ${PRED_COLOR} 0 6px, transparent 6px 10px)`,
                }} />
                <Typography variant="caption">Predicted</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ height: 320 }}>
          <ResponsiveLine
            data={overlaySeries}
            margin={{ top: 10, right: 24, bottom: 40, left: 56 }}
            xScale={{ type: 'linear', min: 0, max: horizonLen - 1 }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
            curve="monotoneX"
            colors={overlayColorFor}
            lineWidth={2.5}
            enablePoints={false}
            useMesh
            enableSlices={false}
            enableArea={false}
            // Layer order: bands behind, then OUR custom dual-line
            // layer (actual SOLID neutral, predicted DASHED brand)
            // replacing nivo's default 'lines' so the two stay
            // distinguishable even when colours overlap and where
            // the lines themselves cross.
            layers={[
              'grid',
              'axes',
              ConfidenceBandLayer,
              AggregateLineLayer,
              'crosshair',
              'mesh',
            ]}
            axisBottom={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Forecast step',
              legendOffset: 32,
              legendPosition: 'middle',
              tickValues: horizonLen > 16 ? 8 : horizonLen,
              format: (v) => Math.round(v) + 1,
            }}
            axisLeft={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Value',
              legendOffset: -46,
              legendPosition: 'middle',
              format: (v) => fmt(v),
            }}
            theme={nivoTheme}
            tooltip={overlayTooltip}
          />
        </Box>
      </Paper>

      {/* ---- Horizon-decay chart ---- */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Error by Forecast Step
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Mean absolute error at each step of the horizon, averaged across windows.
              A flat line means uniform accuracy; a rising line means accuracy degrades further out.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ height: 200 }}>
          <ResponsiveBar
            data={horizonDecay}
            keys={['mae']}
            indexBy="x"
            margin={{ top: 8, right: 24, bottom: 36, left: 56 }}
            colors={[theme.palette.primary.main]}
            borderRadius={2}
            padding={0.25}
            axisBottom={{
              tickSize: 0,
              tickPadding: 6,
              legend: 'Forecast step',
              legendPosition: 'middle',
              legendOffset: 30,
              tickValues: horizonDecay.length > 16
                ? horizonDecay.filter((_, i) => i % Math.ceil(horizonDecay.length / 8) === 0).map((d) => d.x)
                : undefined,
              format: (v) => Number(v) + 1,
            }}
            axisLeft={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Mean |error|',
              legendPosition: 'middle',
              legendOffset: -46,
              format: (v) => fmt(v),
            }}
            enableLabel={false}
            enableGridY
            gridYValues={5}
            theme={nivoTheme}
            tooltip={({ data }) => (
              <Box sx={{
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                px: 1.5, py: 1,
                boxShadow: theme.palette.boxShadow,
              }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Step {data.step} · over {data.n} window{data.n === 1 ? '' : 's'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Mean |error|: {fmt(data.mae)}
                </Typography>
              </Box>
            )}
          />
        </Box>
      </Paper>
    </>
  );

  // Renders the per-window drilldown view (existing behaviour).
  const renderPerWindow = () => (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
      }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
          Validation window
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setWindowIdx((i) => Math.max(0, i - 1))}
            disabled={safeIdx === 0}
            sx={{ minWidth: 32, p: 0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </Button>
          <Typography variant="body2" sx={{ minWidth: 56, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {safeIdx + 1} / {windowCount}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setWindowIdx((i) => Math.min(windowCount - 1, i + 1))}
            disabled={safeIdx >= windowCount - 1}
            sx={{ minWidth: 32, p: 0.5 }}
          >
            <ChevronRightIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      <StatStripGrid stats={winStats} />

      {/* ---- Per-window line chart ---- */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Forecast vs Actual · Window {safeIdx + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 2.5, bgcolor: ACTUAL_COLOR }} />
              <Typography variant="caption">Actual</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{
                width: 14, height: 2.5,
                background: `repeating-linear-gradient(90deg, ${PRED_COLOR} 0 6px, transparent 6px 10px)`,
              }} />
              <Typography variant="caption">Predicted</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ height: 280 }}>
          <ResponsiveLine
            data={lineData}
            margin={{ top: 10, right: 24, bottom: 40, left: 56 }}
            xScale={{ type: 'linear', min: 0, max: groundTruth.length - 1 }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
            curve="monotoneX"
            // colors[] still drives the crosshair / mesh / point
            // colours; the actual line strokes are drawn by
            // PerWindowLineLayer below so the visible Predicted line
            // ends up dashed regardless of theme palette overlap.
            colors={[ACTUAL_COLOR, PRED_COLOR]}
            lineWidth={2.5}
            enablePoints={false}
            useMesh
            enableSlices={false}
            enableArea={false}
            // Replace the default 'lines' layer with our themed
            // dual-line layer (solid Actual, dashed Predicted).
            layers={[
              'grid',
              'axes',
              PerWindowLineLayer,
              'crosshair',
              'mesh',
            ]}
            axisBottom={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Forecast step',
              legendOffset: 32,
              legendPosition: 'middle',
              tickValues: groundTruth.length > 16 ? 8 : groundTruth.length,
              format: (v) => Math.round(v) + 1,
            }}
            axisLeft={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Value',
              legendOffset: -46,
              legendPosition: 'middle',
              format: (v) => fmt(v),
            }}
            theme={nivoTheme}
            tooltip={linePointTooltip}
          />
        </Box>
      </Paper>

      {/* ---- Per-window residuals chart ---- */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Residuals (Predicted − Actual) · Window {safeIdx + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: theme.palette.warning.main }} />
              <Typography variant="caption">Over-prediction</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: theme.palette.error.main }} />
              <Typography variant="caption">Under-prediction</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ height: 160 }}>
          <ResponsiveBar
            data={residualData}
            keys={['residual']}
            indexBy="x"
            margin={{ top: 8, right: 24, bottom: 36, left: 56 }}
            minValue={-Math.max(Math.abs(winStats.maxOver), Math.abs(winStats.maxUnder)) * 1.1 || -1}
            maxValue={Math.max(Math.abs(winStats.maxOver), Math.abs(winStats.maxUnder)) * 1.1 || 1}
            colors={({ data }) => data.color}
            borderRadius={1}
            padding={0.3}
            axisBottom={{
              tickSize: 0,
              tickPadding: 6,
              legend: 'Forecast step',
              legendPosition: 'middle',
              legendOffset: 30,
              tickValues: residualData.length > 16
                ? residualData.filter((_, i) => i % Math.ceil(residualData.length / 8) === 0).map((d) => d.x)
                : undefined,
              format: (v) => Number(v) + 1,
            }}
            axisLeft={{
              tickSize: 4,
              tickPadding: 6,
              format: (v) => fmt(v),
            }}
            enableLabel={false}
            enableGridY
            gridYValues={5}
            markers={[
              {
                axis: 'y',
                value: 0,
                lineStyle: {
                  stroke: alpha(theme.palette.text.primary, 0.5),
                  strokeWidth: 1,
                },
              },
            ]}
            theme={nivoTheme}
            tooltip={({ data }) => {
              const i = Number(data.x);
              const a = groundTruth[i];
              const p = predicted[i];
              const r = data.residual;
              return (
                <Box sx={{
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5, py: 1,
                  boxShadow: theme.palette.boxShadow,
                }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Step {i + 1}
                  </Typography>
                  <Typography variant="body2">Actual: <strong>{fmt(a)}</strong></Typography>
                  <Typography variant="body2">Predicted: <strong>{fmt(p)}</strong></Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: r >= 0 ? theme.palette.warning.main : theme.palette.error.main,
                    }}
                  >
                    Residual: {r >= 0 ? '+' : ''}{fmt(r)}
                  </Typography>
                </Box>
              );
            }}
          />
        </Box>
      </Paper>
    </>
  );

  return (
    <Box>
      {/* ---- Header strip: view toggle + context info ---- */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        mb: 2,
      }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setViewMode(v); }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.5,
              py: 0.4,
              fontSize: '0.8125rem',
              borderColor: theme.palette.divider,
            },
            '& .Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value="aggregate">Aggregate</ToggleButton>
          <ToggleButton value="per-window">Per window</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {windowCount} validation window{windowCount === 1 ? '' : 's'}
          {' · '}
          Horizon: {horizonLen} step{horizonLen === 1 ? '' : 's'}
          {' · '}
          Quantile: {report.val_quantile_used || '0.5 (median)'}
          {' · '}
          {viewMode === 'aggregate'
            ? `Stats over ${allTruth.length} held-out point${allTruth.length === 1 ? '' : 's'}`
            : `Stats over ${groundTruth.length} step${groundTruth.length === 1 ? '' : 's'} in this window`}
        </Typography>
      </Box>

      {viewMode === 'aggregate' ? renderAggregate() : renderPerWindow()}
    </Box>
  );
};

// Predefined reason options for deployment
const reasonOptions = [
  { value: 'testing', label: 'Testing/Validation' },
  { value: 'performance_improvement', label: 'Performance Improvement' },
  { value: 'accuracy_improvement', label: 'Accuracy Improvement' },
  { value: 'model_upgrade', label: 'Model Upgrade' },
  { value: 'scheduled_deployment', label: 'Scheduled Deployment' },
  { value: 'rollback', label: 'Rollback to Previous Version' },
  { value: 'baseline_update', label: 'Baseline Model Update' },
  { value: 'other', label: 'Other (specify below)' }
];

// Predefined configuration options for dedicated deployments
const cpuOptions = [
  { value: '250m', label: 'Light (0.25 cores)', description: 'For testing or low-traffic models' },
  { value: '500m', label: 'Standard (0.5 cores)', description: 'Recommended for most models' },
  { value: '1', label: 'Performance (1 core)', description: 'For high-traffic models' },
  { value: '2', label: 'High Performance (2 cores)', description: 'For demanding models' }
];

const memoryOptions = [
  { value: '512Mi', label: 'Light (512 MB)', description: 'For simple models' },
  { value: '1Gi', label: 'Standard (1 GB)', description: 'Recommended for most models' },
  { value: '2Gi', label: 'Performance (2 GB)', description: 'For complex models' },
  { value: '4Gi', label: 'High Performance (4 GB)', description: 'For large models' }
];

const replicaOptions = [
  { value: 1, label: '1 replica', description: 'Single instance (development/testing)' },
  { value: 2, label: '2 replicas', description: 'Basic redundancy (recommended)' },
  { value: 3, label: '3 replicas', description: 'High availability' },
  { value: 5, label: '5 replicas', description: 'High traffic/critical systems' }
];

// Promotion Dialog Component
const PromotionDialog = ({
  open, onClose, trainingId, modelId, onPromote,
  isFoundation = false,
}) => {
  const theme = useTheme();
  const [deploymentType, setDeploymentType] = useState('shared');
  const [reasonType, setReasonType] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [dedicatedConfig, setDedicatedConfig] = useState({
    cpu: '500m',
    memory: '1Gi',
    replicas: 1
  });

  const handleSubmit = () => {
    // Determine the final reason based on selection
    const finalReason = reasonType === 'other' ? customReason :
                       reasonOptions.find(opt => opt.value === reasonType)?.label || '';

    // For foundation pipelines the server always overrides
    // ``deployment_type`` to foundation regardless of
    // what we send (Triton / KServe can't serve those), so don't
    // bother propagating the FE radio state. Sending the model
    // kind is unnecessary - the BE re-detects it from the model
    // doc. Just send a neutral value the BE happily replaces.
    const effectiveDeploymentType = isFoundation
      ? undefined  // let the BE auto-detect
      : deploymentType;

    onPromote({
      trainingId,
      modelId,
      deploymentType: effectiveDeploymentType,
      reason: finalReason,
      dedicatedConfig:
        (!isFoundation && deploymentType === 'dedicated') ? dedicatedConfig : {},
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <RocketLaunchIcon sx={{ mr: 1 }} />
        Deploy Model
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {isFoundation ? (
            // Foundation models always run on the dedicated foundation
            // server pool with their own fine-tuned LoRA adapter
            // loaded for this user's requests; the Shared/Dedicated
            // distinction doesn't apply (Triton/KServe can't serve
            // foundation models). Surface that as a clear info row
            // rather than a misleading radio.
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Deployment Type · Foundation Server
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Foundation models run on the foundation-server pool with
                your fine-tuned LoRA adapter loaded only for your
                requests. Inference is private to your workspace; the
                shared/dedicated distinction does not apply.
              </Typography>
            </Box>
          ) : (
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Deployment Type</FormLabel>
              <RadioGroup
                value={deploymentType}
                onChange={(e) => setDeploymentType(e.target.value)}
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="shared"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle2">Shared (Public)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Shared node — pay per prediction. Best for low or bursty volume.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="dedicated"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle2">Dedicated</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Dedicated reserved node — flat daily hosting fee, all predictions included. Best for steady high volume.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {!isFoundation && deploymentType === 'dedicated' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Dedicated Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure the resources allocated to your dedicated deployment instance.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="cpu-select-label">CPU</InputLabel>
                    <Select
                      labelId="cpu-select-label"
                      value={dedicatedConfig.cpu}
                      label="CPU"
                      onChange={(e) => setDedicatedConfig(prev => ({ ...prev, cpu: e.target.value }))}
                    >
                      {cpuOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="memory-select-label">Memory</InputLabel>
                    <Select
                      labelId="memory-select-label"
                      value={dedicatedConfig.memory}
                      label="Memory"
                      onChange={(e) => setDedicatedConfig(prev => ({ ...prev, memory: e.target.value }))}
                    >
                      {memoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="replicas-select-label">Replicas</InputLabel>
                    <Select
                      labelId="replicas-select-label"
                      value={dedicatedConfig.replicas}
                      label="Replicas"
                      onChange={(e) => setDedicatedConfig(prev => ({ ...prev, replicas: e.target.value }))}
                    >
                      {replicaOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  💡 <strong>Recommendation:</strong> Start with Standard CPU (0.5 cores) and Standard Memory (1 GB) 
                  with 2 replicas for most production deployments. You can adjust these settings later if needed.
                </Typography>
              </Box>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="reason-select-label">Reason for Deployment</InputLabel>
            <Select
              labelId="reason-select-label"
              value={reasonType}
              label="Reason for Deployment"
              onChange={(e) => setReasonType(e.target.value)}
            >
              {reasonOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {reasonType === 'other' && (
            <TextField
              label="Custom Reason"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Please specify your reason for deployment..."
              required
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <CMButtonOutlined onClick={onClose}>Cancel</CMButtonOutlined>
        <CMButton
          onClick={handleSubmit}
          startIcon={<RocketLaunchIcon />}
          sx={{ ml: 1 }}
        >
          Deploy
        </CMButton>
      </DialogActions>
    </Dialog>
  );
};

const TrainingDetailsModal = ({ open, onClose, trainingId, formatDate, hidePromoteButton = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  
  // Redux state for promotion
  const promotionState = useSelector(state => state.model_promote);
  const { isLoading: isPromoting, error: promotionError, response: promotionResponse } = promotionState;

  // Trained-artifact download (custom / BYOM trainings only).
  const downloadState = useSelector(state => state.training_download);
  const isDownloading = Boolean(downloadState?.isLoading);

  // Training detail redux slice (GET /training/:id via saga). ``loading``
  // mirrors the previous local-state semantics: true while the fetch is
  // in flight AND before any data/error has landed (initial open).
  const detailState = useSelector(state => state.training_detail);
  const trainingDetails = detailState?.response || null;
  const error = detailState?.error || null;
  const loading = Boolean(detailState?.isLoading) || (!trainingDetails && !error);

  // Fetch training details when modal opens and trainingId changes
  useEffect(() => {
    if (open && trainingId) {
      dispatch(trainingDetailRequest({ trainingId }));
    }
  }, [open, trainingId, dispatch]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTabValue(0);
      dispatch(clearState());
      // Drop the fetched details so the next open (possibly a different
      // training, possibly from another mounted instance of this modal)
      // starts from the loading state instead of flashing stale data.
      dispatch(trainingDetailClear());
    }
  }, [open, dispatch]);
  
  // Handle promotion success.
  //
  // ``open`` is part of the guard for a reason: ``model_promote.response``
  // is a global redux slice that persists until something dispatches
  // ``clearState`` -- which only happens when *this* modal closes. Other
  // pages (DeploymentDetailPage, TrainingsTable, ...) keep a hidden
  // <TrainingDetailsModal open={false}/> mounted, so when the user
  // navigates to one of them right after a successful promote, this
  // effect would otherwise see the stale ``promotionResponse`` from
  // the closure and fire another ``navigate('/deployments')``, which
  // looks to the user like the page reloads/jumps unexpectedly. Only
  // act when the modal that triggered the promote is actually open,
  // and clear the slice eagerly so no other instance can re-trigger.
  useEffect(() => {
    if (open && promotionResponse && promotionResponse.success) {
      dispatch(clearState());
      onClose();
      navigate('/deployments');
    }
  }, [open, promotionResponse, onClose, navigate, dispatch]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handlePromote = (promotionData) => {
    dispatch(promoteRequest(promotionData));
  };

  
  // Map every known status string to a real MUI palette key. The
  // fallback MUST be a valid palette entry that has both ``.main`` and
  // ``.contrastText`` (returning ``'grey'`` here would crash:
  //   * MUI Chip with color="grey" hits
  //     ``alpha(theme.palette.grey.main, ...)`` -> grey is a color
  //     scale, not a palette entry, so ``.main`` is undefined ->
  //     ``decomposeColor(undefined)`` throws.
  //   * The status timeline below also reads
  //     ``theme.palette[getStatusColor(...)].main`` -> same issue.
  // Pinning the fallback to ``'info'`` keeps every consumer safe.
  const getStatusColor = (action) => {
    switch (action) {
      case 'success':
      case 'completed':
        return 'success';
      case 'started':
      case 'running':
      case 'in_progress':
        return 'info';
      case 'queued':
      case 'skipped':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const getStatusIcon = (action) => {
    switch (action) {
      case 'success':
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'failed':
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'started':
      case 'running':
      case 'in_progress':
      case 'queued':
        return <CircularProgress size={14} />;
      default:
        return null;
    }
  };
  
  // Get the latest status from the status array. Timestamp ties are
  // broken by lifecycle stage: synthetic rows (e.g. zero-shot
  // champions) stamp queued/started/completed at the same instant,
  // and we must not report such a row as "queued".
  const getLatestStatus = (statusArray) => {
    if (!statusArray || !Array.isArray(statusArray) || statusArray.length === 0) {
      return { action: 'unknown', timestamp: null };
    }

    const rank = {
      queued: 1, skipped: 1,
      started: 2, running: 2, in_progress: 2,
      completed: 3, success: 3, failed: 3, error: 3, cancelled: 3,
    };
    const sortedStatuses = [...statusArray].sort((a, b) => {
      const dt = new Date(b.timestamp) - new Date(a.timestamp);
      if (dt !== 0) return dt;
      return (rank[b.action] || 0) - (rank[a.action] || 0);
    });

    return sortedStatuses[0];
  };
  
  // Format duration in a human-readable way
  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = (seconds % 60).toFixed(1);
      return `${minutes}m ${remainingSeconds}s`;
    }
  };
  
  // Render training timeline
  const renderTimeline = (statusArray) => {
    if (!statusArray || !Array.isArray(statusArray) || statusArray.length === 0) {
      return <Typography variant="body2">No timeline data available</Typography>;
    }
    
    // Sort by timestamp ascending
    const sortedStatuses = [...statusArray].sort((a, b) => {
      return new Date(a.timestamp) - new Date(b.timestamp);
    });
    
    return (
      <Box sx={{ pt: 1 }}>
        {sortedStatuses.map((status, index) => (
          <Box 
            key={index} 
            sx={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              mb: index < sortedStatuses.length - 1 ? 2 : 0,
              position: 'relative'
            }}
          >
            {/* Vertical line connecting status steps */}
            {index < sortedStatuses.length - 1 && (
              <Box sx={{ 
                position: 'absolute',
                left: 15, // Center of the status circle
                top: 30,
                bottom: -20,
                width: 2,
                backgroundColor: theme.palette.divider
              }} />
            )}
            
            {/* Status circle */}
            <Box 
              sx={{ 
                width: 30, 
                height: 30, 
                borderRadius: '50%', 
                bgcolor: theme.palette[getStatusColor(status.action)].main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette[getStatusColor(status.action)].contrastText,
                zIndex: 1,
                mr: 2
              }}
            >
              {getStatusIcon(status.action)}
            </Box>
            
            {/* Status text */}
            <Box>
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                {status.action}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(status.timestamp)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  // Render metrics charts
  const renderMetricsCharts = (report) => {
    if (!report || !report.metrics) {
      return <Typography variant="body2">No metrics data available</Typography>;
    }
    
    const metrics = report.metrics;
    const maxEpochs = report.epochs || 1;

    // If the metrics dict is a flat key/value bag (foundation
    // trainings - no nested ``loss.values`` arrays), render a simple
    // table instead of the per-epoch charts. We detect this by
    // checking whether ANY value is an object with a ``values`` array
    // (the per-epoch shape). If not, treat as flat.
    const looksPerEpoch = Object.values(metrics).some(
      (v) => v && typeof v === 'object' && Array.isArray(v.values)
    );
    if (!looksPerEpoch) {
      const entries = Object.entries(metrics).filter(
        ([, v]) => v !== null && v !== undefined && v !== ''
      );
      if (entries.length === 0) {
        return <Typography variant="body2">No metrics data available</Typography>;
      }
      return (
        <Grid container spacing={2}>
          {entries.map(([key, value]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  boxShadow: theme.palette.lightBoxShadow,
                  bgcolor: alpha(theme.palette.background.card, 0.8),
                  borderRadius: 0,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {key.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="body1">
                  {typeof value === 'number'
                    ? value.toLocaleString(undefined, { maximumFractionDigits: 6 })
                    : String(value)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      );
    }

    // Check if we have loss data
    if (!metrics.loss || !metrics.loss.values) {
      return <Typography variant="body2">No loss metrics available</Typography>;
    }
    
    // Prepare data for loss chart
    const lossData = [];
    
    // Add training loss
    if (metrics.loss.values && Array.isArray(metrics.loss.values)) {
      lossData.push({
        id: 'Loss',
        data: metrics.loss.values.map((value, index) => ({
          x: index + 1,
          y: value
        }))
      });
    }
    
    // Add validation loss - check multiple possible locations
    let valLossValues = null;
    if (metrics.loss.val_values && Array.isArray(metrics.loss.val_values) && metrics.loss.val_values.length > 0) {
      valLossValues = metrics.loss.val_values;
    } else if (metrics.val_loss && metrics.val_loss.values && Array.isArray(metrics.val_loss.values) && metrics.val_loss.values.length > 0) {
      valLossValues = metrics.val_loss.values;
    }
    
    if (valLossValues) {
      lossData.push({
        id: 'Validation Loss',
        data: valLossValues.map((value, index) => ({
          x: index + 1,
          y: value
        }))
      });
    }
    
    // Check if we have MAE data - only include if available
    const maeData = [];
    if (metrics.mean_absolute_error && metrics.mean_absolute_error.values && Array.isArray(metrics.mean_absolute_error.values)) {
      maeData.push({
        id: 'MAE',
        data: metrics.mean_absolute_error.values.map((value, index) => ({
          x: index + 1,
          y: value
        }))
      });
      
      // Add validation MAE if available
      if (metrics.val_mean_absolute_error && metrics.val_mean_absolute_error.values && Array.isArray(metrics.val_mean_absolute_error.values)) {
        maeData.push({
          id: 'Validation MAE',
          data: metrics.val_mean_absolute_error.values.map((value, index) => ({
            x: index + 1,
            y: value
          }))
        });
      }
    }
    
    // Theme-based chart colors
    const chartColors = {
      loss: [theme.palette.primary.main, theme.palette.primary.dark],
      mae: [theme.palette.success.main, theme.palette.success.dark]
    };
    
    // Common chart properties using theme colors
    const chartProps = {
      margin: { top: 10, right: 110, bottom: 40, left: 60 },
      xScale: { type: 'linear', min: 1, max: maxEpochs },
      yScale: { type: 'linear', min: 'auto', max: 'auto' },
      axisBottom: {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Epoch',
        legendOffset: 36,
        legendPosition: 'middle',
        tickTextColor: theme.palette.text.secondary,
        legendTextColor: theme.palette.text.primary
      },
      axisLeft: {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Value',
        legendOffset: -40,
        legendPosition: 'middle',
        tickTextColor: theme.palette.text.secondary,
        legendTextColor: theme.palette.text.primary
      },
      pointSize: 4,
      lineWidth: 2,
      pointColor: { theme: 'background' },
      pointBorderWidth: 2,
      pointBorderColor: { from: 'serieColor' },
      pointLabelYOffset: -12,
      useMesh: true,
      theme: {
        tooltip: {
          container: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.palette.boxShadow,
            borderRadius: 0
          }
        },
        grid: {
          line: {
            stroke: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.6) : theme.palette.divider
          }
        },
        crosshair: {
          line: {
            stroke: theme.palette.primary.main,
            strokeWidth: 1,
            strokeOpacity: 0.5
          }
        },
        axis: {
          domain: {
            line: {
              stroke: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.7) : theme.palette.text.secondary,
              strokeWidth: 1
            }
          },
          ticks: {
            line: {
              stroke: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.7) : theme.palette.text.secondary,
              strokeWidth: 1
            },
            text: {
              fill: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.9) : theme.palette.text.secondary,
              fontSize: 11
            }
          },
          legend: {
            text: {
              fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.primary,
              fontSize: 12,
              fontWeight: 'bold'
            }
          }
        },
        legends: {
          text: {
            fill: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.primary,
            fontSize: 12
          }
        }
      },
      legends: [
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: theme.palette.divider,
          itemTextColor: theme.palette.text.primary,
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: alpha(theme.palette.action.hover, 0.1),
                itemOpacity: 1
              }
            }
          ]
        }
      ]
    };
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={maeData.length > 0 ? 6 : 12}>
          <Typography variant="subtitle2" gutterBottom>Loss</Typography>
          <Box sx={{ 
            height: 240, 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 0, 
            p: 1, 
            bgcolor: theme.palette.background.paper,
            position: 'relative',
            overflow: 'visible',
            zIndex: 1
          }}>
            <ResponsiveLine
              data={lossData}
              {...chartProps}
              colors={chartColors.loss}
              curve="monotoneX"
              tooltip={({ point }) => {
                // Find the corresponding index in both data sets
                const epoch = point.data.x;
                const lossValue = metrics.loss.values[epoch-1] || null;
                const valLossValue = valLossValues ? valLossValues[epoch-1] : null;
                
                return (
                  <div
                    style={{
                      background: theme.palette.background.paper,
                      padding: '8px 12px',
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                      boxShadow: theme.palette.boxShadow,
                      minWidth: '150px'
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                      Epoch: {epoch}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '3px'
                    }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '8px', 
                        height: '8px', 
                        backgroundColor: theme.palette.primary.main,
                        marginRight: '5px' 
                      }}></span>
                      <span>Loss: <strong>{typeof lossValue === 'number' ? lossValue.toFixed(4) : '—'}</strong></span>
                    </div>
                    {valLossValue !== null && (
                      <div style={{ 
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ 
                          display: 'inline-block', 
                          width: '8px', 
                          height: '8px', 
                          backgroundColor: theme.palette.primary.dark,
                          marginRight: '5px' 
                        }}></span>
                        <span>Val Loss: <strong>{typeof valLossValue === 'number' ? valLossValue.toFixed(4) : '—'}</strong></span>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </Box>
        </Grid>
        
        {/* Only show MAE chart if we have MAE data */}
        {maeData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Mean Absolute Error</Typography>
            <Box sx={{ 
              height: 240, 
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: 0, 
              p: 1, 
              bgcolor: theme.palette.background.paper,
              position: 'relative',
              overflow: 'visible',
              zIndex: 1
            }}>
              <ResponsiveLine
                data={maeData}
                {...chartProps}
                colors={chartColors.mae}
                curve="monotoneX"
                tooltip={({ point }) => {
                  const epoch = point.data.x;
                  const maeValue = metrics.mean_absolute_error?.values[epoch-1] || null;
                  const valMaeValue = metrics.val_mean_absolute_error?.values[epoch-1] || null;
                  
                  return (
                    <div
                      style={{
                        background: theme.palette.background.paper,
                        padding: '8px 12px',
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        boxShadow: theme.palette.boxShadow,
                        minWidth: '150px'
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                        Epoch: {epoch}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '3px'
                      }}>
                        <span style={{ 
                          display: 'inline-block', 
                          width: '8px', 
                          height: '8px', 
                          backgroundColor: theme.palette.success.main,
                          marginRight: '5px' 
                        }}></span>
                        <span>MAE: <strong>{typeof maeValue === 'number' ? maeValue.toFixed(4) : '—'}</strong></span>
                      </div>
                      {valMaeValue !== null && (
                        <div style={{ 
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '8px', 
                            height: '8px', 
                            backgroundColor: theme.palette.success.dark,
                            marginRight: '5px' 
                          }}></span>
                          <span>Val MAE: <strong>{typeof valMaeValue === 'number' ? valMaeValue.toFixed(4) : '—'}</strong></span>
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };
  
  // ----- Comparison view ------------------------------------------------
  // Modern, information-dense visualisation of the held-out validation
  // pass. The backend writes one comparison entry per validation
  // window (see ``chronos_bolt.train_lora`` -> ``val_comparisons``).
  // For the user this answers four questions at a glance:
  //   1. How well does the forecast track the actual? (line overlay)
  //   2. WHERE does it fail? (residual chart underneath)
  //   3. How much does it fail? (per-window stat strip: MAE / MSE /
  //      RMSE / MAPE / max over- and undershoot / mean bias)
  //   4. Is the failure systematic or random? (mean bias sign + the
  //      shape of residuals)
  // The window selector lets the user step through every held-out
  // window so they can spot regime-dependent failure modes.
  const renderComparisonChart = (report) => {
    if (!report || !report.individual_comparisons || report.individual_comparisons.length === 0) {
      return (
        <Box sx={{
          py: 4, textAlign: 'center',
          color: theme.palette.text.secondary,
        }}>
          <Typography variant="body2">No comparison data available</Typography>
          <Typography variant="caption">
            (the trainer's held-out validation pass produces this; older
            trainings or very small segments may not have it)
          </Typography>
        </Box>
      );
    }
    return (
      <ComparisonView
        report={report}
        theme={theme}
      />
    );
  };
  
  // Render epochs status table
  const renderEpochsTable = (epochs_status) => {
    if (!epochs_status || !Array.isArray(epochs_status) || epochs_status.length === 0) {
      return <Typography variant="body2">No epochs data available</Typography>;
    }
    
    // Check what properties are available in the first epoch to determine columns
    const firstEpoch = epochs_status[0];
    const availableColumns = [];
    
    // Number-safe ``.toFixed`` formatter. Epoch payloads can carry
    // string sentinels (e.g. legacy "training_failed") - calling
    // ``.toFixed`` on those crashes the table; render '—' instead.
    const fmt5 = (val) => (typeof val === 'number' ? val.toFixed(5) : '—');

    // Always show epoch, loss, val_loss, and timestamp if they exist
    if (firstEpoch.epoch !== undefined) availableColumns.push({ key: 'epoch', label: 'Epoch', format: (val) => val });
    if (firstEpoch.loss !== undefined) availableColumns.push({ key: 'loss', label: 'Loss', format: fmt5 });
    if (firstEpoch.val_loss !== undefined) availableColumns.push({ key: 'val_loss', label: 'Val Loss', format: fmt5 });

    // Conditionally add other metrics if they exist
    if (firstEpoch.mean_absolute_error !== undefined) {
      availableColumns.push({ key: 'mean_absolute_error', label: 'MAE', format: fmt5 });
    }
    if (firstEpoch.val_mean_absolute_error !== undefined) {
      availableColumns.push({ key: 'val_mean_absolute_error', label: 'Val MAE', format: fmt5 });
    }
    if (firstEpoch.mean_squared_error !== undefined) {
      availableColumns.push({ key: 'mean_squared_error', label: 'MSE', format: fmt5 });
    }
    if (firstEpoch.val_mean_squared_error !== undefined) {
      availableColumns.push({ key: 'val_mean_squared_error', label: 'Val MSE', format: fmt5 });
    }
    
    // Always show timestamp if it exists
    if (firstEpoch.timestamp !== undefined) {
      availableColumns.push({ key: 'timestamp', label: 'Time', format: (val) => formatDate(val) });
    }
    
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {availableColumns.map((column) => (
                <TableCell key={column.key}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {epochs_status.map((epoch) => (
              <TableRow key={epoch.epoch} hover>
                {availableColumns.map((column) => (
                  <TableCell key={column.key}>
                    {column.format(epoch[column.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  if (loading) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.palette.boxShadow
          }
        }}
      >
        <DialogTitle>Model Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        </DialogContent>
        <DialogActions>
          <CMButtonOutlined onClick={onClose} startIcon={<CloseIcon />}>Close</CMButtonOutlined>
        </DialogActions>
      </Dialog>
    );
  }
  
  if (error) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.palette.boxShadow
          }
        }}
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" color="error" gutterBottom>Failed to load training details</Typography>
            <Typography variant="body1">{error}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <CMButtonOutlined onClick={onClose} startIcon={<CloseIcon />}>Close</CMButtonOutlined>
        </DialogActions>
      </Dialog>
    );
  }
  
  if (!trainingDetails) {
    return null;
  }
  
  // Get the latest status
  const latestStatus = getLatestStatus(trainingDetails.status);
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.palette.boxShadow
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        py: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flexGrow: 1,
          minWidth: 0
        }}>
          <Typography 
            variant="h6" 
            component="div"
            sx={{
              wordBreak: 'break-all',
              maxWidth: 'none',
              overflow: 'visible',
              whiteSpace: 'normal',
              lineHeight: 1.2
            }}
          >
            Model Details: {trainingDetails.uid || 'N/A'}
          </Typography>
          <Chip 
            label={latestStatus.action} 
            color={getStatusColor(latestStatus.action)} 
            size="small" 
            icon={getStatusIcon(latestStatus.action)}
            sx={{ ml: 2, flexShrink: 0 }}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(trainingDetails.created_at)}
          </Typography>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ bgcolor: theme.palette.background.default }}>
        {/* Training Summary */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                boxShadow: theme.palette.lightBoxShadow,
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Training Duration</Typography>
                  <Typography variant="body1">{formatDuration(trainingDetails.duration)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {latestStatus.action}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Pipeline ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: theme.typography.fontFamily.monospace, fontSize: '0.875rem' }}>
                    {trainingDetails.model_id ? trainingDetails.model_id.substring(0, 12) + '...' : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Model</Typography>
                  <Typography variant="body1" sx={{ fontFamily: theme.typography.fontFamily.monospace, fontSize: '0.875rem' }}>
                    {trainingDetails.blueprint ? trainingDetails.blueprint.substring(0, 12) + '...' : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Training ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: theme.typography.fontFamily.monospace, fontSize: '0.875rem' }}>
                    {trainingDetails.uid ? trainingDetails.uid.substring(0, 12) + '...' : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Framework</Typography>
                  <Typography variant="body1">
                    {/* Backend resolves this per training_kind (e.g.
                        PyTorch for foundation, blueprint metadata for
                        custom). Empty string falls through to em-dash
                        so we don't lie. */}
                    {trainingDetails.framework || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Model Type</Typography>
                  <Typography variant="body1">
                    {trainingDetails.model_type || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Cost</Typography>
                  <Typography variant="body1">
                    {/* Cost is denominated in CREDITS, not USD - see
                        core/config/credit_costs.yaml. The backend
                        also returns ``cost_unit`` ('credits') so
                        future unit changes don't require an FE edit. */}
                    {trainingDetails.cost !== '' && trainingDetails.cost !== null && trainingDetails.cost !== undefined
                      ? `${parseFloat(trainingDetails.cost).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${trainingDetails.cost_unit || 'credits'}`
                      : `0 ${trainingDetails.cost_unit || 'credits'}`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Model Size</Typography>
                  <Typography variant="body1">
                    {trainingDetails.model_size || '—'}
                  </Typography>
                </Grid>
                {Array.isArray(trainingDetails.deployments) && trainingDetails.deployments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Deployment
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {trainingDetails.deployments.map((d) => {
                        const dtLabel = (() => {
                          switch (d.deployment_type) {
                            case 'shared':
                            case 'modelmesh_shared':
                              return 'Shared';
                            case 'dedicated':
                            case 'kserve_dedicated':
                              return 'Dedicated';
                            case 'foundation':
                              return 'Foundation · shared';
                            case 'external_fm':
                              return 'External FM · shared';
                            default:
                              return d.deployment_type || '—';
                          }
                        })();
                        const status = d.status || '';
                        return (
                          <Box
                            key={d.promotion_id || d.deployment_type}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {dtLabel}
                            </Typography>
                            {status && (
                              <Typography
                                variant="caption"
                                sx={{
                                  textTransform: 'capitalize',
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                }}
                              >
                                · {status}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                )}
                {trainingDetails.message && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Message</Typography>
                    <Typography variant="body1">{trainingDetails.message}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                height: '100%',
                boxShadow: theme.palette.lightBoxShadow,
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Performance
              </Typography>
              {/* Two unified 0-100 metrics, written by every trainer
                  (legacy custom + foundation)
                  so users can compare trainings across model kinds:
                    * Accuracy  - WMAPE-derived headline ("how close
                                  to actuals on aggregate?")
                    * Skill (R\u00b2) - secondary "does the model add
                                  value over a constant baseline?"
                                  Catches mode collapse where Accuracy
                                  alone would look generous.
                  Kind-specific extras (loss, MAE, rank, etc.) render
                  below for context, not as the headline number. */}
              {(() => {
                const accuracy = trainingDetails.report?.accuracy_score;
                const skill = trainingDetails.report?.skill_score;
                const hasAccuracy = typeof accuracy === 'number';
                const hasSkill = typeof skill === 'number';
                const comparisons = Array.isArray(trainingDetails.report?.individual_comparisons)
                  ? trainingDetails.report.individual_comparisons : [];
                const evalWindows = comparisons.length;
                const evalPoints = comparisons.reduce(
                  (n, c) => n + (Array.isArray(c?.ground_truth) ? c.ground_truth.length : 0), 0,
                );
                const colorFor = (v) => v >= 80
                  ? theme.palette.success.main
                  : v >= 60
                  ? theme.palette.primary.main
                  : v >= 40
                  ? theme.palette.warning.main
                  : theme.palette.error.main;
                const accuracyColor = hasAccuracy ? colorFor(accuracy) : theme.palette.text.secondary;
                const skillColor = hasSkill ? colorFor(skill) : theme.palette.text.secondary;
                const accuracyHint = !hasAccuracy
                  ? 'no held-out comparison data yet'
                  : accuracy >= 80
                  ? 'excellent fit'
                  : accuracy >= 60
                  ? 'good fit'
                  : accuracy >= 40
                  ? 'rough fit'
                  : 'poor fit';
                const skillHint = !hasSkill
                  ? 'needs comparisons'
                  : skill >= 70
                  ? 'beats baseline'
                  : skill >= 40
                  ? 'modest signal'
                  : skill >= 10
                  ? 'weak signal'
                  : 'near baseline';
                return (
                  <Grid container spacing={2}>
                    {/* Headline: Accuracy (WMAPE) + Skill (R\u00b2)
                        side-by-side. Accuracy is the big number;
                        Skill is the smaller diagnostic with a tooltip. */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box sx={{
                          flex: 2,
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha(accuracyColor, 0.05),
                        }}>
                          <AccuracyInfoTooltip
                            value={hasAccuracy ? accuracy : null}
                            scoreKind={trainingDetails.report?.score_kind}
                            backtest={{ windows: evalWindows, points: evalPoints }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{
                              textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
                              cursor: 'help', borderBottom: `1px dotted ${theme.palette.divider}`,
                            }}>
                              Accuracy
                            </Typography>
                          </AccuracyInfoTooltip>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: accuracyColor, lineHeight: 1.1, mt: 0.25 }}
                          >
                            {hasAccuracy ? `${accuracy.toFixed(1)}%` : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary, display: 'block', mt: 0.25,
                          }}>
                            {accuracyHint}
                          </Typography>
                        </Box>
                        <Box sx={{
                          flex: 1,
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha(skillColor, 0.04),
                        }}>
                          <Tooltip
                            arrow
                            placement="top"
                            title="Skill vs the naive baseline: how much the model's error beats carrying the last value forward on the same held-out data. 0% = no better than the baseline. High Accuracy with 0% Skill usually means the data is calm — the model isn't adding value beyond the baseline."
                          >
                            <Typography variant="caption" color="text.secondary" sx={{
                              textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
                              cursor: 'help', borderBottom: `1px dotted ${theme.palette.divider}`,
                            }}>
                              Skill
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: skillColor, lineHeight: 1.1, mt: 0.25 }}
                          >
                            {hasSkill ? `${skill.toFixed(1)}%` : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary, display: 'block', mt: 0.25,
                          }}>
                            {skillHint}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* Kind-specific context. For foundation trainings
                        we surface loss/MAE/rank/size; for legacy
                        custom we surface MAE/epochs/final loss. */}
                    {trainingDetails.training_kind === 'foundation' ? (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Validation MAE</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.val_mae === 'number'
                              ? trainingDetails.report.val_mae.toFixed(2)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Validation MSE</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.val_mse === 'number'
                              ? trainingDetails.report.val_mse.toFixed(2)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">LoRA Rank</Typography>
                          <Typography variant="body1">
                            {trainingDetails.report?.rank || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Adapter Size</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.size_mb === 'number'
                              ? `${trainingDetails.report.size_mb.toFixed(2)} MB`
                              : '—'}
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Custom-blueprint extras. Defensive
                            ``typeof === 'number'`` guards because the
                            legacy "training failed" fallback path
                            writes string sentinels like
                            ``final_loss: "training_failed"`` into the
                            report - calling ``.toFixed()`` on those
                            crashes the whole modal. */}
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Mean Absolute Error</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.comparison_metrics?.mean_absolute_error === 'number'
                              ? trainingDetails.report.comparison_metrics.mean_absolute_error.toFixed(2)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Final Loss</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.final_loss === 'number'
                              ? trainingDetails.report.final_loss.toFixed(5)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Epochs</Typography>
                          <Typography variant="body1">
                            {typeof trainingDetails.report?.epochs === 'number'
                              ? trainingDetails.report.epochs
                              : '—'}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                );
              })()}
            </Paper>
          </Grid>
        </Grid>
        
        {/* Tabs for detailed information */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: theme.palette.background.paper,
          borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
        }}>
          {/* Tab list is built dynamically so the Epochs tab is hidden
              for trainings with no per-epoch data (foundation)
              instead of showing an empty tab. ``has_epochs`` is set
              server-side. */}
          {(() => {
            const tabs = [
              { key: 'comparison', label: 'Comparison', icon: <SpeedIcon /> },
              { key: 'timeline', label: 'Timeline', icon: <ScheduleIcon /> },
              { key: 'metrics', label: 'Metrics', icon: <TrendingUpIcon /> },
            ];
            if (trainingDetails.has_epochs) {
              tabs.push({ key: 'epochs', label: 'Epochs', icon: <MemoryIcon /> });
            }
            // Clamp the active tab if it points past the visible set
            // (e.g. user was on Epochs before the FE re-rendered with
            // it hidden).
            const safeTabValue = Math.min(tabValue, tabs.length - 1);
            return (
              <>
                <Tabs
                  value={safeTabValue}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    sx: { backgroundColor: theme.palette.primary.main },
                  }}
                  sx={{
                    '& .MuiTab-root': {
                      color: theme.palette.text.secondary,
                      '&.Mui-selected': {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  {tabs.map((t) => (
                    <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start" />
                  ))}
                </Tabs>
              </>
            );
          })()}
        </Box>
        
        <Box sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
          mb: 2
        }}>
          {/* Tab indices match the dynamic tab list above. Epochs is
              only rendered when ``has_epochs`` is true so the panel
              index aligns with the tab index in the visible set. */}
          {/* Comparison Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="subtitle2" gutterBottom>
              Model Prediction vs. Ground Truth
            </Typography>
            {renderComparisonChart(trainingDetails.report)}
            {trainingDetails.report?.comparison_metrics && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Comparison Metrics</Typography>
                <Grid container spacing={2}>
                  {Object.entries(trainingDetails.report.comparison_metrics).map(([key, value]) => (
                    <Grid item xs={12} sm={4} key={key}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          boxShadow: theme.palette.lightBoxShadow,
                          bgcolor: alpha(theme.palette.background.card, 0.8),
                          borderRadius: 0
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">{key}</Typography>
                        <Typography variant="body1">{typeof value === 'number' ? value.toFixed(4) : String(value)}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </TabPanel>
          
          {/* Timeline Tab */}
          <TabPanel value={tabValue} index={1}>
            {renderTimeline(trainingDetails.status)}
          </TabPanel>
          
          {/* Metrics Tab */}
          <TabPanel value={tabValue} index={2}>
            {renderMetricsCharts(trainingDetails.report)}
          </TabPanel>
          
          {/* Epochs Tab - only mounted when has_epochs is true. */}
          {trainingDetails.has_epochs && (
            <TabPanel value={tabValue} index={3}>
              {renderEpochsTable(trainingDetails.epochs_status)}
            </TabPanel>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        {/* Show promotion error if exists */}
        {promotionError && (
          <Alert severity="error" sx={{ flex: 1, mr: 2 }}>
            {promotionError.message || promotionError}
          </Alert>
        )}
        
        
        {/* Trained artifacts are exportable for custom (and BYOM)
            trainings that completed serialization. Foundation LoRA
            trainings reference shared pretrained weights and are
            excluded server-side too. */}
        {trainingDetails.training_kind === 'custom' && trainingDetails.executed && (
          <CMButtonOutlined
            onClick={() => dispatch(downloadTraining({ training_id: trainingId }))}
            startIcon={isDownloading
              ? <CircularProgress size={14} color="inherit" />
              : <FileDownloadOutlinedIcon />}
            disabled={isDownloading}
            sx={{ mr: 'auto' }}
          >
            {isDownloading ? 'Preparing…' : 'Download Model'}
          </CMButtonOutlined>
        )}

        {!hidePromoteButton && (() => {
          // Promote is enabled when the backend explicitly says the
          // training is ready_for_promotion (set by the foundation
          // worker on success), OR - for legacy custom trainings
          // that don't write the flag - when the latest status
          // action is the terminal success state. Accept both
          // "success" (legacy custom trainer) and "completed"
          // (foundation trainer) so we don't grey out the button
          // depending on which trainer ran.
          const isTerminalSuccess =
            latestStatus.action === 'success' ||
            latestStatus.action === 'completed';
          const canPromote = Boolean(
            trainingDetails.ready_for_promotion || isTerminalSuccess
          );
          return (
            <CMButton
              onClick={() => setPromotionDialogOpen(true)}
              startIcon={<RocketLaunchIcon />}
              disabled={isPromoting || !canPromote}
              sx={{ mr: 1 }}
            >
              {isPromoting ? 'Deploying...' : 'Deploy Model'}
            </CMButton>
          );
        })()}
        
        <CMButtonOutlined
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          Close
        </CMButtonOutlined>
      </DialogActions>
      
      {/* Promotion Dialog. Foundation-mode pipelines don't use a
          blueprint document - we pass that fact down so the dialog
          can hide the Shared/Dedicated radio (the choice is
          meaningless for foundation models, which always run on the
          foundation server pool regardless of selection). */}
      <PromotionDialog
        open={promotionDialogOpen}
        onClose={() => setPromotionDialogOpen(false)}
        trainingId={trainingId}
        modelId={trainingDetails.model_id}
        isFoundation={!trainingDetails.blueprint}
        onPromote={handlePromote}
      />
    </Dialog>
  );
};

export default TrainingDetailsModal; 